import dayjs from 'dayjs'
import unserialize from 'locutus/php/var/unserialize'
import siteConfig from 'site.config'

const generateRandomString = () => {
  const array = new Uint8Array(16)
  crypto.getRandomValues(array)
  return Array.from(array, byte => byte.toString(36)).join('')
}

const getRandomValues = () => {
  const array = new Uint32Array(1)
  return crypto.getRandomValues(array)[0]
}

/**
 * Date formatting using day.js
 * @param {*} date – a timestamp
 */
const dateFormat = (date, format = 'MMM D, YYYY') => {
  return dayjs(date).format(format)
}

const convertMillisecondToSecond = value => {
  let result = value
  if (typeof value === 'number') {
    // Heuristic: Assume values above 10^10 are in milliseconds
    if (value > Math.pow(10, 10)) {
      result = Math.floor(value / 1000) // Convert to seconds
    } else {
      result = value
    }
  }
  return result
}

/**
 * Gets a particular value,
 * if undefined, null or error it returns a default value.
 * Use this function to get Object values safely.
 *
 * ie: const myVar = getValue(() => someObject.someProp.SomeChildProp, defaultsTo);
 *
 * @param value (any) - The value to get
 * @param defaultsTo (any) - The value to defaults to
 * @return any
 */
const getValue = (value, defaultsTo) => {
  let safeValue

  try {
    safeValue = value()
  } catch (e) {
    console.log('getValue:', e.message)
    return defaultsTo
  }

  return isDefined(safeValue) && safeValue !== null ? safeValue : defaultsTo
}

/**
 * Checks if a value is defined
 *
 * @param value (any) - The value to check for
 * @return boolean
 */
const isDefined = value => {
  return value !== undefined
}

/**
 * Checks if a value is empty
 *
 * @param value (any) - The value to test
 * @return boolean
 */
const isEmpty = value => {
  return (
    !isDefined(value) ||
    value === '' ||
    value === false ||
    value === 0 ||
    isEmptyObject(value) ||
    value === null
  )
}

/**
 * Checks if obj is an empty Object
 *
 * @param obj (object) - The object to test
 * @return boolean
 */
const isEmptyObject = obj => {
  if (typeof obj != 'object') {
    return false
  }

  return getValue(() => Object.keys(obj).length, 0) <= 0
}

/**
 * Checks if value is an integer
 *
 * @param value (any) - The value to test
 * @return boolean
 */
const isInteger = value => {
  return Number.isInteger(value)
}

/**
 * A function that converts an unserialized PHP string.
 *
 * @param {string} text
 *
 * @returns the object with the corresponding properties.
 */
const unserializeText = text => {
  return unserialize(text)
}

const downloadCsvFromData = (headers, data, fileName) => {
  const csvRows = []
  const headerValues = headers.map(header => header)
  csvRows.push(headerValues.join(',')) // Push into array as comma separated values
  forEach(data, singleRow => {
    let row = headerValues.map(key => {
      const escaped = ('' + singleRow[key]).replaceAll('"', String.raw`\"`) // To replace the unwanted quotes.
      return `"${escaped}"`
    })
    csvRows.push(row)
  })
  let csvData = csvRows.join('\n')
  const blob = new Blob([csvData], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.setAttribute('hidden', '')
  a.setAttribute('href', url)
  a.setAttribute('download', fileName + '.csv')
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/**
 * A function that helps completing tracking URLs
 *
 * @param {string} url
 * @param {string} secure
 * @returns {string} the url modificated if needed
 */
const transformTrackingUrl = (url, secure = false) => {
  if (!url.includes('//')) {
    const protocol = secure ? 'https' : 'http'
    url = `${protocol}://${url}/`
  }
  if (!url.includes('.php')) {
    url = url + 'piwik.php'
  }
  return url
}

/**
 * Gets a particular value,
 * if undefined, null or error it returns a default value.
 * Use this function to get Object values safely.
 *
 * ie: const myVar = getValue(() => someObject.someProp.SomeChildProp, defaultsTo);
 *
 * @param value (any) - The value to get
 * @param defaultsTo (any) - The value to defaults to
 * @return any
 */
const getSafeValue = (value, defaultsTo) => {
  return !isEmpty(value) && value !== null ? value : defaultsTo
}

/**
 * Decode JWT token
 *
 * @param token (string) - JWT token
 * @return any
 */
const decodeJWT = token => {
  const base64Payload = token.split('.')[1]
  const payload = window.atob(base64Payload)
  return JSON.parse(payload.toString())
}

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]
const weekdays = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
]

const transform = (obj, predicate) => {
  return Object.keys(obj).reduce((memo, key) => {
    if (predicate(obj[key], key)) {
      memo[key] = obj[key]
    }
    return memo
  }, {})
}
// Omit removed in Lodash 5
const omit = (obj, items) => transform(obj, (v, key) => !items.includes(key))
const pick = (obj, items) => transform(obj, (v, key) => items.includes(key))

const onMenuOpen = className => {
  setTimeout(() => {
    const selectedEl = document.getElementsByClassName(
      `${className}__option--is-selected`
    )[0]
    if (selectedEl && typeof selectedEl.scrollIntoView === 'function') {
      selectedEl?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      })
    }
  }, 15)
}

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

const getRandomInt = (min, max) => {
  // Ensure min and max are integers and min is less than max
  min = Math.ceil(min)
  max = Math.floor(max)

  // Create an array to hold a single random byte
  const array = new Uint8Array(1)
  crypto.getRandomValues(array)

  // Convert the byte to a float between 0 and 1 (inclusive of 0, exclusive of 1)
  const randomFloat = array[0] / 256

  // Scale and map the random float to the desired range
  return Math.floor(randomFloat * (max - min + 1)) + min
}

const getSiteConfig = (path, defaultValue) => {
  return get(siteConfig, path, defaultValue)
}

/**
 * A function that will return a previous timestamp from a given timestamp.
 * Used for queries to FB-ES.
 *
 * @param {*} date – given timestamp
 * @param {*} number – amount of {frame}
 * @param {*} unit – 'y', 'M', 'w', 'd'
 * @returns a timestamp
 */
const priorDate = (date, number, unit) => {
  return dayjs(date)
    .subtract(Number.parseInt(number), unit)
    .unix()
}
/**
 * @returns the 'to' and 'from' values for the queries
 * @param frame – should be in the form '{number}{unit}', e.g. '6M', '2W'
 */
function getTimeRange(frame) {
  frame = frame.split(/([DWMY])/gi)
  let to = Date.now()
  const from = priorDate(
    to,
    frame[0],
    frame[1] === 'M' ? 'M' : frame[1].toLowerCase()
  )
  to = Math.round(to / 1000)
  return { to, from }
}

function getProfileInfo(profile) {
  const partyId = get(profile, 'info.user.partyId', '')
  const partyEmailId = get(profile, 'info.user.partyEmailId', '')
  const professionId = get(profile, 'info.user.professionId', '')
  const specialtyId = get(profile, 'info.user.specialtyId', '')
  const specialtyIdLima = get(profile, 'info.user.specialtyIdLima', '')
  const countryCode = get(profile, 'info.user.countryCode', '')
  return {
    partyId,
    partyEmailId,
    professionId,
    specialtyId,
    specialtyIdLima,
    countryCode
  }
}

const scrollToAnchor = (id, offset = 0) => {
  const element = document.getElementById(id)

  if (element) {
    const rect = element.getBoundingClientRect()
    const scrollTop = document.documentElement.scrollTop
    const position = rect.top + scrollTop - offset

    window.scrollTo({
      top: position,
      behavior: 'smooth'
    })
  }
}

function get(object, path, defaultValue) {
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g)

  const result = pathArray.reduce((accumulator, key) => {
    return accumulator !== undefined && accumulator !== null
      ? accumulator[key]
      : undefined
  }, object)

  return result === undefined ? defaultValue : result
}

function padEnd(string, length, chars = ' ') {
  string = String(string)
  if (string.length >= length) {
    return string
  }

  const padLength = length - string.length
  const padString = chars
    .repeat(Math.ceil(padLength / chars.length))
    .slice(0, padLength)

  return string + padString
}

function forEach(collection, iteratee) {
  if (Array.isArray(collection)) {
    for (let i = 0; i < collection.length; i++) {
      // Call the iteratee with the element, index, and the entire collection
      iteratee(collection[i], i, collection)
    }
  } else if (typeof collection === 'object' && collection !== null) {
    for (let key in collection) {
      if (collection.hasOwnProperty(key)) {
        // Call the iteratee with the value, key, and the entire collection
        iteratee(collection[key], key, collection)
      }
    }
  }
}

function forOwn(object, iteratee) {
  // Ensure the input is an object and not null
  if (typeof object === 'object' && object !== null) {
    for (let key in object) {
      if (Object.hasOwn(object, key)) {
        // Pass the value, key, and object itself to the iteratee
        iteratee(object[key], key, object)
      }
    }
  }
}

function floor(number, precision = 0) {
  const factor = Math.pow(10, precision)
  return Math.floor(number * factor) / factor
}

function concat(array, ...values) {
  const result = array.slice() // Create a shallow copy of the initial array

  for (const value of values) {
    if (Array.isArray(value)) {
      result.push(...value) // Flatten the value if it's an array
    } else {
      result.push(value) // Add the value directly if it's not an array
    }
  }

  return result
}

function map(collection, iteratee) {
  const result = []

  if (Array.isArray(collection)) {
    for (let i = 0; i < collection.length; i++) {
      result.push(iteratee(collection[i], i, collection))
    }
  } else if (collection !== null && typeof collection === 'object') {
    for (const key in collection) {
      if (collection.hasOwnProperty(key)) {
        result.push(iteratee(collection[key], key, collection))
      }
    }
  }

  return result
}

function groupBy(collection, iteratee) {
  const result = {}

  // Determine the appropriate iteratee function if it's not already a function
  const getKey =
    typeof iteratee === 'function' ? iteratee : item => item[iteratee]

  for (const value of collection) {
    const key = getKey(value)

    if (!result[key]) {
      result[key] = []
    }

    result[key].push(value)
  }

  return result
}

function orderBy(collection, iteratees, orders) {
  const directions = {
    asc: 1,
    desc: -1
  }

  // Create a function to get the value by iteratee
  const getValue = (item, iteratee) =>
    typeof iteratee === 'function' ? iteratee(item) : item[iteratee]

  // Sort the collection
  return collection.slice().sort((a, b) => {
    for (let i = 0; i < iteratees.length; i++) {
      const iteratee = iteratees[i]
      const order = directions[orders[i]] || directions.asc
      const valueA = getValue(a, iteratee)
      const valueB = getValue(b, iteratee)

      if (valueA < valueB) return -order
      if (valueA > valueB) return order
    }
    return 0
  })
}

function sumBy(collection, iteratee) {
  // Helper function to get the value by iteratee
  const getValue = item =>
    typeof iteratee === 'function' ? iteratee(item) : item[iteratee]

  // Calculate the sum
  return collection.reduce((accumulator, current) => {
    return accumulator + getValue(current)
  }, 0)
}

let placeholderKeyCounter = 0

const createPlaceholderKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()

  if (window.crypto?.getRandomValues) {
    const bytes = new Uint32Array(4)
    window.crypto.getRandomValues(bytes)

    placeholderKeyCounter =
      (placeholderKeyCounter + 1) % Number.MAX_SAFE_INTEGER

    const hex = Array.from(bytes, n => n.toString(16).padStart(8, '0')).join('')

    return `${Date.now()}-${hex}-${placeholderKeyCounter}`
  }

  // Final fallback (should be rare): stable within session, no Math.random.
  placeholderKeyCounter += 1
  return `${Date.now()}-${placeholderKeyCounter}`
}

/**
 * PropTypes validator generator for a number constrained to a range.
 *
 * Usage:
 *   progress: between({ gte: 0, lte: 100 })
 *   strokeWidth: between({ gte: 1, lte: 10 })
 */
const between = ({ gte = -Infinity, lte = Infinity } = {}) => {
  const validate = (props, propName, componentName, location, propFullName) => {
    const name = propFullName || propName
    const value = props[propName]

    // Allow undefined/null (typical PropTypes pattern)
    if (value === undefined || value === null) return null

    if (typeof value !== 'number' || Number.isNaN(value)) {
      return new Error(
        `Invalid ${location} \`${name}\` of value \`${value}\` supplied to ` +
          `\`${componentName}\`, expected a number.`
      )
    }

    if (value < gte || value > lte) {
      return new Error(
        `Invalid ${location} \`${name}\` of value \`${value}\` supplied to ` +
          `\`${componentName}\`, expected a number between ${gte} and ${lte}.`
      )
    }

    return null
  }

  // Provide `.isRequired` for parity with other PropTypes validators.
  validate.isRequired = (
    props,
    propName,
    componentName,
    location,
    propFullName
  ) => {
    const name = propFullName || propName
    const value = props[propName]

    if (value === undefined || value === null) {
      return new Error(
        `The ${location} \`${name}\` is marked as required in ` +
          `\`${componentName}\`, but its value is \`${value}\`.`
      )
    }

    return validate(props, propName, componentName, location, propFullName)
  }

  return validate
}

export {
  generateRandomString,
  dateFormat,
  convertMillisecondToSecond,
  isInteger,
  isEmptyObject,
  isEmpty,
  isDefined,
  between,
  transformTrackingUrl,
  getValue,
  getSafeValue,
  unserializeText,
  downloadCsvFromData,
  decodeJWT,
  monthNames,
  weekdays,
  omit,
  pick,
  onMenuOpen,
  delay,
  getRandomInt,
  getRandomValues,
  getSiteConfig,
  priorDate,
  getTimeRange,
  getProfileInfo,
  scrollToAnchor,
  get,
  padEnd,
  forEach,
  forOwn,
  floor,
  concat,
  map,
  groupBy,
  orderBy,
  sumBy,
  createPlaceholderKey
}
